import React, { useState } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { Input } from '../../auth/components/Input';
import { CountrySelect } from '../../../core/components/CountrySelect';
import { SectorSelect } from '../../../core/components/SectorSelect';
import { EmployeeRangeSelect } from '../../../core/components/EmployeeRangeSelect';
import { AddSectorModal } from '../../admin/companies/components/AddSectorModal';
import { Sector, SectorCreatePayload } from '../../admin/companies/types';

export interface CompanyFormState {
  name: string;
  domain: string;
  description: string;
  website: string;
  industry: string;
  location: string;
  employees: string;
  founding_year: string;
  sector_id: number | null;
}

interface OnboardingStepProps {
  companyForm: CompanyFormState;
  onInputChange: (field: keyof CompanyFormState, value: string) => void;
  onSectorChange: (value: number | null) => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  sectors: Sector[];
  onCreateSector: (payload: SectorCreatePayload) => Promise<Sector | null>;
}

export const OnboardingStep: React.FC<OnboardingStepProps> = ({
  companyForm,
  onInputChange,
  onSectorChange,
  onSave,
  saving,
  error,
  sectors,
  onCreateSector,
}) => {
  const [showAddSector, setShowAddSector] = useState(false);

  const handleCreateSector = async (payload: SectorCreatePayload) => {
    const created = await onCreateSector(payload);
    if (!created) throw new Error('Failed to create sector');
    onSectorChange(created.id);
  };

  const fieldStyle = {
    borderColor: '#E5E7EB',
    backgroundColor: '#F2F2F2',
    color: COLORS.primary.persianIndigo,
  };

  return (
    <div className="flex-1">
      <div className="mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: COLORS.extended.azure + '15' }}>
          <svg className="w-5 h-5" style={{ color: COLORS.extended.azure }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-1" style={{ color: COLORS.primary.persianIndigo }}>
          Complete Company Profile
        </h2>
        <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
          Start your journey in streamlined portfolio indexing
        </p>
      </div>

      <div className="space-y-5">
        <div className="space-y-4">
          <Input
            label="Company Name"
            type="text"
            value={companyForm.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            placeholder="Enter company name"
            required
          />

          <Input
            label="Domain"
            type="text"
            value={companyForm.domain}
            onChange={(e) => onInputChange('domain', e.target.value)}
            placeholder="example.com"
            required
          />

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Description <span style={{ color: COLORS.extended.redSalsa }}>*</span>
            </label>
            <textarea
              value={companyForm.description}
              onChange={(e) => onInputChange('description', e.target.value)}
              rows={4}
              placeholder="Enter company description"
              required
              className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:ring-2 transition-all"
              style={{
                borderColor: companyForm.description ? 'transparent' : COLORS.extended.redSalsa,
                backgroundColor: '#F2F2F2',
                color: COLORS.primary.persianIndigo,
              }}
              onFocus={(e) => { e.target.style.borderColor = COLORS.extended.azure; }}
              onBlur={(e) => { e.target.style.borderColor = companyForm.description ? 'transparent' : COLORS.extended.redSalsa; }}
            />
          </div>

          <Input
            label="Website"
            type="url"
            value={companyForm.website}
            onChange={(e) => onInputChange('website', e.target.value)}
            placeholder="https://example.com"
            required
          />

          <Input
            label="Industry"
            type="text"
            value={companyForm.industry}
            onChange={(e) => onInputChange('industry', e.target.value)}
            placeholder="e.g., Technology, Finance, Healthcare"
            required
          />

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Sector
            </label>
            <SectorSelect
              sectors={sectors}
              value={companyForm.sector_id}
              onChange={onSectorChange}
              onAddSector={() => setShowAddSector(true)}
              borderColor={fieldStyle.borderColor}
              placeholder={sectors.length === 0 ? 'No sectors yet — click below to add one' : 'Select a sector...'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Location <span style={{ color: COLORS.extended.redSalsa }}>*</span>
            </label>
            <CountrySelect
              value={companyForm.location}
              onChange={(value) => onInputChange('location', value)}
              borderColor={fieldStyle.borderColor}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.primary.persianIndigo }}>
              Employees <span style={{ color: COLORS.extended.redSalsa }}>*</span>
            </label>
            <EmployeeRangeSelect
              value={companyForm.employees}
              onChange={(value) => onInputChange('employees', value)}
              borderColor={fieldStyle.borderColor}
            />
          </div>

          <Input
            label="Founding Year"
            type="number"
            value={companyForm.founding_year}
            onChange={(e) => onInputChange('founding_year', e.target.value)}
            placeholder="e.g., 2020"
            required
          />
        </div>

        {error && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.extended.redSalsa + '20' }}>
            <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>
              {error}
            </p>
          </div>
        )}

        <div className="pt-4">
          <button
            onClick={onSave}
            disabled={saving}
            className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: COLORS.extended.azure }}
          >
            {saving ? 'Saving...' : 'Next'}
          </button>
        </div>
      </div>

      <AddSectorModal
        isOpen={showAddSector}
        onClose={() => setShowAddSector(false)}
        onSubmit={handleCreateSector}
      />
    </div>
  );
};
